import React, { useState } from 'react';
import { ExternalLink, Calculator, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import SportsbookLogo, { SPORTSBOOK_NAMES, SPORTSBOOK_URLS } from './SportsbookLogo';
import TeamLogo from './TeamLogo';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function formatOdds(odds) {
  if (odds == null) return '—';
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function formatDollars(n) {
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
}

function naturalRound(amount) {
  if (amount == null) return null;
  const increment = amount >= 1000 ? 10 : amount >= 100 ? 5 : 1;
  return Math.round(amount / increment) * increment;
}

function oddsToDecimal(american) {
  if (!american) return 2;
  if (american > 0) return american / 100 + 1;
  return 100 / Math.abs(american) + 1;
}

function getSportIcon(sportKey) {
  if (!sportKey) return '🏆';
  if (sportKey.includes('football')) return '🏈';
  if (sportKey.includes('basketball')) return '🏀';
  if (sportKey.includes('hockey')) return '🏒';
  if (sportKey.includes('soccer')) return '⚽';
  if (sportKey.includes('tennis')) return '🎾';
  if (sportKey.includes('mma')) return '🥊';
  if (sportKey.includes('baseball')) return '⚾';
  if (sportKey.includes('boxing')) return '🥊';
  return '🏆';
}

function getMarketLabel(market) {
  if (!market || market === 'h2h') return 'Moneyline';
  if (market === 'spreads') return 'Spread';
  if (market === 'totals') return 'Total';
  if (market === 'props') return 'Props';
  return market;
}

function getSideLabel(side, market) {
  if (!side) return 'Side';
  if (market === 'totals') return side.startsWith('Over') ? 'Over' : 'Under';
  const teamPart = market === 'spreads' ? side.replace(/\s[+-]\d+\.?\d*$/, '') : side;
  return teamPart.split(' ').pop();
}

function formatSide(side, market) {
  if (!side) return side;
  if (market === 'spreads') {
    // "New York Mets +1.5"  →  name + line
    const match = side.match(/^(.*?)(\s[+-]\d+\.?\d*)$/);
    if (match) {
      return <>{match[1]}<span className="pc-line">{match[2]}</span></>;
    }
  }
  if (market === 'totals') {
    // "Over 8.5" or "Under 8.5"
    const match = side.match(/^(Over|Under)\s+(\d+\.?\d*)$/);
    if (match) {
      return <><span className="pc-ou">{match[1]}</span><span className="pc-line"> {match[2]}</span></>;
    }
  }
  return side;
}

function BookLink({ bookKey, bookTitle }) {
  return (
    <div className="pc-book-row">
      <SportsbookLogo bookKey={bookKey} size={16} />
      <span className="pc-book-name">{SPORTSBOOK_NAMES[bookKey] || bookTitle}</span>
      {SPORTSBOOK_URLS[bookKey] && (
        <a
          href={SPORTSBOOK_URLS[bookKey]}
          target="_blank"
          rel="noopener noreferrer"
          className="book-link"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function PromoCard({ opportunity, session, qualifyingBetId, onTracked, noteStage, promoId }) {
  const opp = opportunity;
  const sportIcon = getSportIcon(opp.sport);
  const [tracked, setTracked] = useState(false);
  const [trackingMode, setTrackingMode] = useState(false);
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');
  const [showCalc, setShowCalc] = useState(false);
  const [calcMode, setCalcMode] = useState('promo');
  const [customPromo, setCustomPromo] = useState('');
  const [customHedge, setCustomHedge] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedPromoOdds, setEditedPromoOdds] = useState('');
  const [editedHedgeOdds, setEditedHedgeOdds] = useState('');

  const effectivePromoOdds = opp.boosted_odds ?? opp.promo_odds;
  const dPromo = oddsToDecimal(effectivePromoOdds);
  const dHedge = oddsToDecimal(opp.hedge_odds);

  const parsedEditPromo = parseInt(editedPromoOdds);
  const parsedEditHedge = parseInt(editedHedgeOdds);
  const activePromoOdds = editMode && !isNaN(parsedEditPromo) && parsedEditPromo !== 0 ? parsedEditPromo : effectivePromoOdds;
  const activeDPromo = oddsToDecimal(activePromoOdds);
  const activeHedgeOdds = editMode && !isNaN(parsedEditHedge) && parsedEditHedge !== 0 ? parsedEditHedge : opp.hedge_odds;
  const activeDHedge = oddsToDecimal(activeHedgeOdds);
  const isFreeMoney = opp.type === 'free_bet' || opp.type === 'bet_and_get';
  const activeHedgeAmount = editMode
    ? naturalRound(isFreeMoney
        ? naturalRound(opp.promo_amount) * (activeDPromo - 1) / activeDHedge
        : naturalRound(opp.promo_amount) * activeDPromo / activeDHedge)
    : opp.hedge_amount;

  const aA = parseFloat(amountA) || 0;
  const aB = parseFloat(amountB) || 0;
  const isFreeMoneyA = opp.type === 'free_bet' || opp.type === 'bet_and_get';
  const profitIfPromoWins = aA * (dPromo - 1) - aB;
  const profitIfHedgeWins = isFreeMoneyA
    ? aB * (dHedge - 1)
    : aB * (dHedge - 1) - aA;

  // Per-outcome breakdown using the same rounded amounts shown in the step display
  const pA = naturalRound(opp.promo_amount || 0);
  const pH = naturalRound(activeHedgeAmount || 0);
  const cardPromoWins = pA * (activeDPromo - 1) - pH;
  const cardHedgeWins = isFreeMoneyA
    ? pH * (activeDHedge - 1)
    : pH * (activeDHedge - 1) - pA;
  const promoLabel = getSideLabel(opp.promo_side, opp.market);
  const hedgeLabel = getSideLabel(opp.hedge_side, opp.market);
  const showPreview = aA > 0 || aB > 0;
  const labelA = (opp.promo_side || 'Promo').split(' ').pop();
  const labelB = (opp.hedge_side || 'Hedge').split(' ').pop();

  // Calculator derived values
  let calcResult = null;
  if (calcMode === 'promo') {
    const pAmt = naturalRound(parseFloat(customPromo) || 0);
    if (pAmt > 0) {
      const hCalc = naturalRound(isFreeMoneyA ? pAmt * (dPromo - 1) / dHedge : pAmt * dPromo / dHedge);
      calcResult = {
        promoAmt: pAmt, hedgeAmt: hCalc,
        promoWins: pAmt * (dPromo - 1) - hCalc,
        hedgeWins: isFreeMoneyA ? hCalc * (dHedge - 1) : hCalc * (dHedge - 1) - pAmt,
      };
    }
  } else {
    const hAmt = naturalRound(parseFloat(customHedge) || 0);
    if (hAmt > 0) {
      const pCalc = naturalRound(isFreeMoneyA ? hAmt * dHedge / (dPromo - 1) : hAmt * dHedge / dPromo);
      calcResult = {
        promoAmt: pCalc, hedgeAmt: hAmt,
        promoWins: pCalc * (dPromo - 1) - hAmt,
        hedgeWins: isFreeMoneyA ? hAmt * (dHedge - 1) : hAmt * (dHedge - 1) - pCalc,
      };
    }
  }

  async function handleTrack(e) {
    e.stopPropagation();
    if (!session) return;
    // Determine raw sides for auto-resolve
    const rawPromoSide = opp.market === 'totals'
      ? (opp.promo_side?.startsWith('Over') ? 'Over' : 'Under')
      : opp.market === 'spreads'
      ? (opp.home_team && opp.promo_side?.startsWith(opp.home_team) ? opp.home_team : opp.away_team) || opp.promo_side
      : opp.promo_side;
    const rawHedgeSide = opp.market === 'totals'
      ? (opp.hedge_side?.startsWith('Over') ? 'Over' : 'Under')
      : opp.market === 'spreads'
      ? (opp.home_team && opp.hedge_side?.startsWith(opp.home_team) ? opp.home_team : opp.away_team) || opp.hedge_side
      : opp.hedge_side;
    try {
      await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'promo',
          sportsbook: opp.promo_book,
          eventName: opp.event,
          sportKey: opp.sport,
          market: opp.market || 'h2h',
          side: opp.promo_side,
          odds: activePromoOdds,
          amount: parseFloat(amountA) || 0,
          sportsbookB: opp.hedge_book,
          sideB: opp.hedge_side,
          oddsB: activeHedgeOdds,
          amountB: parseFloat(amountB) || 0,
          notes: JSON.stringify({
            event_id: opp.event_id,
            sport_key: opp.sport,
            commence_time: opp.commence_time,
            promo_type: opp.type,
            home_team: opp.home_team,
            away_team: opp.away_team,
            point: opp.point ?? null,
            side_a: rawPromoSide,
            side_b: rawHedgeSide,
            ...(qualifyingBetId
              ? { stage: 'bonus', qualifying_bet_id: qualifyingBetId }
              : noteStage ? { stage: noteStage, ...(promoId ? { promo_id: promoId } : {}) } : {}),
          }),
        }),
      });
      setTrackingMode(false);
      setTracked(true);
      if (onTracked) onTracked();
    } catch (err) {
      console.error('Track failed:', err);
    }
  }

  // Color tier
  let tier = 'low';
  if (opp.type === 'free_bet' || opp.type === 'bet_and_get') {
    const rate = opp.type === 'bet_and_get' ? opp.net_conversion_rate : opp.conversion_rate;
    tier = rate >= 35 ? 'high' : rate >= 20 ? 'medium' : 'low';
  } else if (opp.type === 'no_sweat') {
    const rp = opp.return_pct ?? 0;
    tier = rp >= 15 ? 'high' : rp >= 8 ? 'medium' : 'low';
  } else if (opp.type === 'profit_boost') {
    tier = opp.ev_percentage >= 5 ? 'high' : opp.ev_percentage >= 2 ? 'medium' : 'low';
  } else {
    tier = opp.net_bonus_value >= opp.promo_amount * 0.8 ? 'high'
         : opp.net_bonus_value >= opp.promo_amount * 0.6 ? 'medium' : 'low';
  }

  // Hero dollar amount — always the primary focus
  const lockAmount = opp.type === 'deposit_bonus'
    ? opp.net_bonus_value
    : opp.type === 'bet_and_get'
    ? opp.net_profit
    : opp.guaranteed_profit;

  const displayLockAmount = editMode && (opp.type === 'free_bet' || opp.type === 'no_sweat' || opp.type === 'profit_boost')
    ? Math.min(cardPromoWins, cardHedgeWins)
    : lockAmount;

  // Secondary metric shown alongside
  const secondaryLabel = opp.type === 'free_bet'
    ? `${opp.conversion_rate}% of $${opp.promo_amount} free bet`
    : opp.type === 'no_sweat'
    ? `${opp.return_pct}% ROI · ~$${opp.free_bet_value?.toFixed(0)} free bet if loss`
    : opp.type === 'profit_boost'
    ? `+${opp.ev_percentage}% EV`
    : opp.type === 'bet_and_get'
    ? `${opp.net_conversion_rate}% net · gross $${opp.guaranteed_profit?.toFixed(2)}`
    : `keep after $${opp.expected_cost?.toFixed(2)} clearing cost`;

  // Step 1: what the user places at the promo book
  const step1OddsDisplay = editMode
    ? <input
        className="odds-edit-input"
        value={editedPromoOdds}
        onChange={e => setEditedPromoOdds(e.target.value)}
        placeholder={String(effectivePromoOdds)}
        onClick={e => e.stopPropagation()}
      />
    : opp.type === 'profit_boost' && opp.boosted_odds != null
    ? <><span className="pc-odds-orig">{formatOdds(opp.promo_odds)}</span><span className="pc-odds-arrow">→</span><span className="pc-odds-boosted">{formatOdds(opp.boosted_odds)}</span></>
    : <span className="pc-odds">{formatOdds(opp.promo_odds)}</span>;

  const step1AmountLabel = (opp.type === 'free_bet' || opp.type === 'bet_and_get')
    ? <><span className="pc-amount">${naturalRound(opp.promo_amount)}</span><span className="pc-amount-note">free bet</span></>
    : opp.type === 'deposit_bonus'
    ? <><span className="pc-amount">${naturalRound(opp.total_to_wager)}</span><span className="pc-amount-note">to wager</span></>
    : opp.type === 'no_sweat'
    ? <><span className="pc-amount">${naturalRound(opp.promo_amount)}</span><span className="pc-amount-note">real $</span></>
    : <span className="pc-amount">${naturalRound(opp.promo_amount)}</span>;

  return (
    <div className={`promo-card promo-${tier}`}>

      {/* Compact header */}
      <div className="pc-header">
        <span className="pc-sport">{sportIcon} {opp.sport_title || 'Sports'}</span>
        <span className="pc-market">{getMarketLabel(opp.market)}</span>
      </div>

      {/* Event */}
      <div className="pc-event">
        <span className="mini-game-team"><TeamLogo teamName={opp.home_team} sportKey={opp.sport} size={13} />{opp.home_team}</span>
        <span className="mini-game-sep">vs</span>
        <span className="mini-game-team"><TeamLogo teamName={opp.away_team} sportKey={opp.sport} size={13} />{opp.away_team}</span>
      </div>
      {opp.commence_time && (
        <div className="pc-game-time">
          {new Date(opp.commence_time).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
          })}
        </div>
      )}

      {/* Player info for props */}
      {opp.market === 'props' && opp.player_team && (
        <div className="ev-mini-player-info">
          {opp.player_jersey && <span className="ev-mini-player-jersey">#{opp.player_jersey}</span>}
          <TeamLogo teamName={opp.player_team} sportKey={opp.sport} size={13} />
          <span className="ev-mini-player-team">{opp.player_team}</span>
        </div>
      )}

      {/* HERO — locked profit */}
      <div className={`pc-hero pc-hero-${tier}`}>
        <div className="pc-hero-main">
          <span className="pc-hero-label">{opp.type === 'bet_and_get' ? 'Net profit' : 'Lock in'}</span>
          <span className="pc-hero-amount">{formatDollars(displayLockAmount)}</span>
        </div>
        <span className="pc-hero-secondary">{secondaryLabel}</span>
      </div>

      {/* bet_and_get: show qualifying cost context */}
      {opp.type === 'bet_and_get' && (
        <div className="pc-qualifying-note">
          Qualifying cost: −{formatDollars(opp.qualifying_cost)}
        </div>
      )}

      {/* no_sweat: note that a free bet arrives on loss */}
      {opp.type === 'no_sweat' && (
        <div className="pc-qualifying-note pc-ns-note">
          If your bet loses, add a ${opp.promo_amount} Free Bet promo to convert it
        </div>
      )}

      {/* Step 1 */}
      <div className="pc-step">
        <span className="pc-step-num">1</span>
        <div className="pc-step-body">
          <BookLink bookKey={opp.promo_book} bookTitle={opp.promo_book_title} />
          <div className="pc-step-bet">
            <span className="pc-side">{formatSide(opp.promo_side, opp.market)}</span>
            <div className="pc-step-odds-amount">
              {step1OddsDisplay}
              <span className="pc-divider">·</span>
              {step1AmountLabel}
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 */}
      <div className="pc-step">
        <span className="pc-step-num">2</span>
        <div className="pc-step-body">
          <BookLink bookKey={opp.hedge_book} bookTitle={opp.hedge_book_title} />
          <div className="pc-step-bet">
            <span className="pc-side">{formatSide(opp.hedge_side, opp.market)}</span>
            <div className="pc-step-odds-amount">
              {editMode ? (
                <input
                  className="odds-edit-input"
                  value={editedHedgeOdds}
                  onChange={e => setEditedHedgeOdds(e.target.value)}
                  placeholder={String(opp.hedge_odds)}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <span className="pc-odds">{formatOdds(opp.hedge_odds)}</span>
              )}
              <span className="pc-divider">·</span>
              <span className="pc-amount">${activeHedgeAmount != null ? naturalRound(activeHedgeAmount) : '—'}</span>
              <span className="pc-amount-note">real $</span>
            </div>
          </div>
        </div>
      </div>

      <div className="edit-odds-row">
        {!editMode && <span className="edit-odds-hint">Seeing different odds?</span>}
        <button
          className="edit-odds-btn"
          onClick={e => {
            e.stopPropagation();
            if (editMode) { setEditedPromoOdds(''); setEditedHedgeOdds(''); }
            setEditMode(v => !v);
          }}
        >
          <Pencil size={11} />
          {editMode ? 'Reset odds' : 'Edit here'}
        </button>
      </div>
      <button className="arb-calc-toggle" onClick={() => setShowCalc(!showCalc)}>
        <Calculator size={16} />
        Bet Calculator
        {showCalc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showCalc ? (
        <div className="arb-calculator">
          <div className="calc-mode-tabs">
            <button className={calcMode === 'promo' ? 'active' : ''} onClick={() => setCalcMode('promo')}>
              Fix Promo Bet
            </button>
            <button className={calcMode === 'hedge' ? 'active' : ''} onClick={() => setCalcMode('hedge')}>
              Fix Hedge Bet
            </button>
          </div>
          <div className="calc-body">
            <div className="calc-input-group">
              <label>{calcMode === 'promo' ? 'Amount on promo side:' : 'Amount on hedge side:'}</label>
              <div className="calc-input-wrap">
                <span className="calc-currency">$</span>
                {calcMode === 'promo' ? (
                  <input
                    type="number"
                    placeholder={String(naturalRound(opp.promo_amount || 100))}
                    value={customPromo}
                    onChange={(e) => setCustomPromo(e.target.value)}
                    min="0"
                  />
                ) : (
                  <input
                    type="number"
                    placeholder={String(naturalRound(opp.hedge_amount || 50))}
                    value={customHedge}
                    onChange={(e) => setCustomHedge(e.target.value)}
                    min="0"
                  />
                )}
              </div>
            </div>
            {calcResult && (
              <div className="calc-results">
                <div className="calc-row promo-calc-row">
                  <div className="promo-calc-book">
                    <BookLink bookKey={opp.promo_book} bookTitle={opp.promo_book_title} />
                    <span className="promo-calc-side">{formatSide(opp.promo_side, opp.market)}</span>
                  </div>
                  <span className="calc-value">${calcResult.promoAmt}</span>
                </div>
                <div className="calc-row promo-calc-row">
                  <div className="promo-calc-book">
                    <BookLink bookKey={opp.hedge_book} bookTitle={opp.hedge_book_title} />
                    <span className="promo-calc-side">{formatSide(opp.hedge_side, opp.market)}</span>
                  </div>
                  <span className="calc-value">${calcResult.hedgeAmt}</span>
                </div>
                <div className={`calc-row calc-profit ${calcResult.promoWins <= 0 ? 'calc-negative' : ''}`}>
                  <span>If {promoLabel} wins:</span>
                  <span className={`calc-value ${calcResult.promoWins > 0 ? 'profit-value' : 'loss-value'}`}>
                    {calcResult.promoWins >= 0 ? '+' : ''}${calcResult.promoWins.toFixed(2)}
                  </span>
                </div>
                <div className={`calc-row calc-profit ${calcResult.hedgeWins <= 0 ? 'calc-negative' : ''}`}>
                  <span>If {hedgeLabel} wins:</span>
                  <span className={`calc-value ${calcResult.hedgeWins > 0 ? 'profit-value' : 'loss-value'}`}>
                    {calcResult.hedgeWins >= 0 ? '+' : ''}${calcResult.hedgeWins.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        pA > 0 && pH > 0 && (
          <div className="arb-profit-preview">
            <div className="arb-profit-preview-row">
              <span>{promoLabel} wins:</span>
              <span className={cardPromoWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                {cardPromoWins >= 0 ? '+' : ''}${cardPromoWins.toFixed(2)}
              </span>
            </div>
            <div className="arb-profit-preview-row">
              <span>{hedgeLabel} wins:</span>
              <span className={cardHedgeWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                {cardHedgeWins >= 0 ? '+' : ''}${cardHedgeWins.toFixed(2)}
              </span>
            </div>
          </div>
        )
      )}

      {session && !tracked && !trackingMode && (
        <button
          className="ev-track-btn"
          onClick={(e) => {
            e.stopPropagation();
            setAmountA(calcResult ? String(calcResult.promoAmt) : (opp.promo_amount ? String(naturalRound(opp.promo_amount)) : ''));
            setAmountB(calcResult ? String(calcResult.hedgeAmt) : (activeHedgeAmount != null ? String(naturalRound(activeHedgeAmount)) : ''));
            setTrackingMode(true);
          }}
        >
          Track Bet
        </button>
      )}
      {session && trackingMode && (
        <div className="arb-track-form" onClick={e => e.stopPropagation()}>
          <div className="arb-track-row">
            <span className="arb-track-label">{opp.promo_book_title || opp.promo_book}</span>
            <input
              type="number"
              className="track-amount-input"
              value={amountA}
              onChange={e => setAmountA(e.target.value)}
              placeholder="Promo stake ($)"
              min="0"
              step="0.01"
              autoFocus
            />
          </div>
          <div className="arb-track-row">
            <span className="arb-track-label">{opp.hedge_book_title || opp.hedge_book}</span>
            <input
              type="number"
              className="track-amount-input"
              value={amountB}
              onChange={e => setAmountB(e.target.value)}
              placeholder="Hedge stake ($)"
              min="0"
              step="0.01"
            />
          </div>
          {showPreview && (
            <div className="arb-profit-preview">
              <div className="arb-profit-preview-row">
                <span>{labelA} wins:</span>
                <span className={profitIfPromoWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                  {profitIfPromoWins >= 0 ? '+' : ''}${profitIfPromoWins.toFixed(2)}
                </span>
              </div>
              <div className="arb-profit-preview-row">
                <span>{labelB} wins:</span>
                <span className={profitIfHedgeWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                  {profitIfHedgeWins >= 0 ? '+' : ''}${profitIfHedgeWins.toFixed(2)}
                </span>
              </div>
            </div>
          )}
          <div className="track-amount-btns">
            <button className="track-amount-save" onClick={handleTrack}>Track</button>
            <button className="track-amount-cancel" onClick={(e) => { e.stopPropagation(); setTrackingMode(false); }}>✕</button>
          </div>
        </div>
      )}
      {session && tracked && (
        <button className="ev-track-btn ev-track-btn--done" onClick={e => e.stopPropagation()} disabled>Tracked ✓</button>
      )}

    </div>
  );
}

export default PromoCard;
